import { islandRequiresInternalSupports, syncAccessorySkuCount } from '@rta/rta-blueprint-library';
import type { Island, Kitchen } from '@rta/rta-blueprint-library';

/** A customization the blueprint team assigns to specific islands. */
export type IslandAddon = {
	key: string;
	label: string;
	/** Parked feature: visible but disabled in the UI, renders nothing, emits nothing. */
	comingSoon?: boolean;
	/** Catalog accessory this mirrors — assigning the add-on also puts the SKU on the estimate. */
	sku: string | null;
	/** Derived add-ons are decided by the model; shown read-only so the state is visible in one place. */
	derived: boolean;
	hint: string;
};

export const LEVELING_LEGS_SKU = 'Leveling-Legs';

/** ADA accessible COUNTER maximum (ADA 902). Survey of 9 real ADA designs (4 customers): 8/9 are base 32.75" + 1.25" top = 34.0" even; see bp-feature-corpus/ada/FINDINGS.md. */
export const ADA_COUNTER_HEIGHT = 34;
const DEFAULT_COUNTERTOP_THICKNESS = 1.25;

/** One override key shape for every add-on, so reader and writer can never drift. */
export const getIslandAddonKey = (addonKey: string, islandId: string): string => `addon.${addonKey}.${islandId}`;

export const ISLAND_ADDONS: Array<IslandAddon> = [
	{ key: 'levelingLegs', label: 'Leveling legs', sku: LEVELING_LEGS_SKU, derived: false, hint: 'NSC line + MFG panel note; keeps the estimate accessory in sync' },
	{ key: 'adaSizing', label: 'ADA counter height', sku: null, derived: false, hint: `Sets the COUNTER to the ${ADA_COUNTER_HEIGHT}" ADA maximum (base = ${ADA_COUNTER_HEIGHT}" − top thickness)` },
	{ key: 'utilityLines', label: 'Utility stub-ups', sku: null, derived: false, comingSoon: true, hint: 'Coming soon — gas-line stub-up points per the owner mockup (#466); routing needs more design' },
	{ key: 'landingZone', label: 'Landing zone', sku: null, derived: false, hint: 'Adds a Landing Zones page at the end of the BP. Auto-on for Scratch Coat islands (Island Sizes Playbook: scratch coat requires landing zones)' },
	{ key: 'internalSupports', label: 'Internal support panels', sku: null, derived: false, hint: 'Adds a duplicate side-panel pair sheet (#733). Auto-on over 30" base depth and for Smoker stands; finalized BPs show it is designer-controlled, so untick to drop it' },
	{ key: 'ventCutouts', label: 'Vent cutouts', sku: null, derived: true, hint: 'Vents are present on this island' },
	{ key: 'seams', label: 'Countertop seams', sku: null, derived: true, hint: 'This island carries a breakpoint seam' }
];

// Single source of truth lives in the library beside the toggled-off validation rule.
export const islandNeedsInternalSupports = (island: Island): boolean => islandRequiresInternalSupports(island);

export const islandHasVents = (island: Island): boolean =>
	island.panels.children.some((panel) =>
		panel.panelItemGroups.children.some((group) =>
			group.items.children.some((panelItem) => panelItem.item.category?.isVent === true)));

// Seams are COUNTERTOP breakpoints; panel breakpoints are MFG panel sections — a different thing.
export const islandHasSeams = (kitchen: Kitchen, island: Island): boolean =>
	kitchen.design.countertops.filterByIslandId(island.id).some((countertop) => countertop.breakpoints.children.length > 0);

/** The fuelled appliances on an island, for the utility-lines NSC listing. */
export const getFuelledSkus = (island: Island): Array<string> => {
	const skus: Array<string> = [];
	island.panels.children.forEach((panel) =>
		panel.panelItemGroups.children.forEach((group) =>
			group.items.children.forEach((panelItem) => {
				const { item } = panelItem;
				if (item.fuel && item.sku && !skus.includes(item.sku)) skus.push(item.sku);
			})));
	return skus;
};

const hasAccessory = (kitchen: Kitchen, sku: string): boolean =>
	(kitchen.accessories?.children ?? []).some((accessory) => (accessory.sku ?? '').toLowerCase() === sku.toLowerCase());

/**
 * Resolves whether an add-on is on for an island.
 * Leveling legs: an explicit per-island value wins; otherwise the kitchen-wide
 * accessory decides, so a designer adding the SKU on the estimate defaults every
 * island in — matching how the note behaved before per-island control existed.
 */
export const getIslandAddonState = (kitchen: Kitchen, island: Island, addonKey: string): boolean => {
	switch (addonKey) {
		case 'internalSupports': {
			// Finalized BPs show depth > 30" is the default, NOT a mandate: 8 of 9 deep-island
			// BPs ship without the sheet, and 28"-deep smoker stands ship WITH it. Explicit wins.
			const explicit = kitchen.blueprintOverrides?.[getIslandAddonKey(addonKey, island.id)];
			if (explicit === '1') return true;
			if (explicit === '0') return false;
			return islandNeedsInternalSupports(island);
		}
		case 'ventCutouts': return islandHasVents(island);
		case 'seams': return islandHasSeams(kitchen, island);
		case 'levelingLegs': {
			const explicit = kitchen.blueprintOverrides?.[getIslandAddonKey(addonKey, island.id)];
			if (explicit === '1') return true;
			if (explicit === '0') return false;
			// The designer-added accessory defaults islands on ONLY while nothing has been
			// chosen per island; one explicit choice anywhere ends the default, so ticking
			// island A (which syncs the accessory) can never flip B on behind your back.
			const anyExplicit = kitchen.design.islands.children
				.some((candidate) => kitchen.blueprintOverrides?.[getIslandAddonKey(addonKey, candidate.id)] !== undefined);
			return !anyExplicit && hasAccessory(kitchen, LEVELING_LEGS_SKU);
		}
		case 'adaSizing': return kitchen.blueprintOverrides?.[getIslandAddonKey(addonKey, island.id)] !== undefined;
		case 'landingZone': {
			// Island Sizes Playbook: "Scratch Coat (Require Landing Zones for appliances.)" — the
			// finish mandates the page; any other finish is designer opt-in. Explicit always wins.
			const explicit = kitchen.blueprintOverrides?.[getIslandAddonKey(addonKey, island.id)];
			if (explicit === '1') return true;
			if (explicit === '0') return false;
			return island.surround?.material === 'scratchcoat';
		}
		default: return kitchen.blueprintOverrides?.[getIslandAddonKey(addonKey, island.id)] === '1';
	}
};

/**
 * Applies an add-on change. Leveling legs keep the estimate accessory in sync;
 * ADA sizing stores the prior height in the override value so unticking restores it.
 */
export const setIslandAddonState = (kitchen: Kitchen, island: Island, addonKey: string, on: boolean): void => {
	const addon = ISLAND_ADDONS.find(({ key }) => key === addonKey);
	if (!addon || addon.derived || addon.comingSoon) return;

	const key = getIslandAddonKey(addonKey, island.id);

	if (addonKey === 'levelingLegs') {
		const islands = kitchen.design.islands.children;
		const keyOf = (candidate: Island): string => getIslandAddonKey('levelingLegs', candidate.id);

		// A designer-added accessory defaults every island on. The first BP-team touch
		// materializes that default as explicit per-island values, so unticking one
		// island can never silently flip the others.
		const anyExplicit = islands.some((candidate) => kitchen.blueprintOverrides?.[keyOf(candidate)] !== undefined);
		if (!anyExplicit && hasAccessory(kitchen, LEVELING_LEGS_SKU)) {
			islands.forEach((candidate) => kitchen.setBlueprintOverride(keyOf(candidate), '1'));
		}

		kitchen.setBlueprintOverride(key, on ? '1' : '0');

		// Estimate sync via the rules-engine standard: one accessory per qualifying
		// island, add-only — persisted state wins on load, the user removes manually
		// through the accessories UI (sync-accessory-sku-count.ts).
		const qualifyingCount = islands.filter((candidate) => kitchen.blueprintOverrides?.[keyOf(candidate)] === '1').length;
		syncAccessorySkuCount({ kitchen, sku: LEVELING_LEGS_SKU, qualifyingCount });
		return;
	}

	if (addonKey === 'adaSizing') {
		// Size-locked islands (Corner/Pizza/Asado — every non-Grill/Bar/Smoker type) reject
		// resizes, so toggling would store the override while the island stays put. Found
		// live on a locked island: checkbox on, height unchanged. Refuse instead.
		if (island.sizeLocked) return;
		const stored = kitchen.blueprintOverrides?.[key];
		if (on && stored === undefined) {
			// The 34" limit is the finished COUNTER (base + top): #543's real design is base 32.75" + 1.25" top.
			const topThickness = kitchen.material?.countertop?.thickness;
			const thickness = typeof topThickness === 'number' && topThickness > 0 ? topThickness : DEFAULT_COUNTERTOP_THICKNESS;
			kitchen.setBlueprintOverride(key, String(island.size.height));
			island.size = { ...island.size, height: ADA_COUNTER_HEIGHT - thickness };
		}
		if (!on && stored !== undefined) {
			const previous = Number(stored);
			if (Number.isFinite(previous) && previous > 0) island.size = { ...island.size, height: previous };
			kitchen.clearBlueprintOverride(key);
		}
		return;
	}

	if (addonKey === 'internalSupports' || addonKey === 'landingZone') {
		// '0' must be stored, not cleared: clearing falls back to the auto-default (>30" depth /
		// scratch-coat finish), which would instantly re-tick the box the designer just turned off.
		kitchen.setBlueprintOverride(key, on ? '1' : '0');
		return;
	}

	if (on) kitchen.setBlueprintOverride(key, '1');
	else kitchen.clearBlueprintOverride(key);
};

export const anyIslandHasAddon = (kitchen: Kitchen, addonKey: string): boolean =>
	kitchen.design.islands.children.some((island) => getIslandAddonState(kitchen, island, addonKey));
